import "./SkeletonCards.css"
import "../RoomCards/RoomCards.css"
import { useRef, useState } from "react"
import Skeleton from "src/shared/components/layout/skeleton/Skeleton"
import {
    ROOMS_GRID_SKELETON,
    ROOMS_LENGTH_SKELETON,
    ROOMS_POSITIONS_SKELETON,
    ROOMS_SIZE_SKELETON,
} from "src/shared/constants/localStorage"
import { useSetSkeletonCardSize } from "./hooks/useSetSkeletonCardSize"
import { RoomCardSizes } from "../RoomCard/RoomCard"

type SavedPosition = { x: number; y: number }
/** Mismo formato que `roomsDimensions`: `x` son las filas y `y` las columnas. */
type SavedGrid = { x: number; y: number }

/** Solo se usa en el modo de respaldo (flex-wrap), donde no hay celda que llenar. */
const CARD_SIZES: Record<RoomCardSizes, { width: number; height: number }> = {
    xs: { width: 106, height: 70 },
    sm: { width: 129, height: 92 },
    md: { width: 161, height: 118 },
    lg: { width: 263, height: 103 },
    xl: { width: 264, height: 242 },
    mxl: { width: 264, height: 242 },
}

function read<T>(key: string, fallback: T): T {
    try {
        return JSON.parse(localStorage.getItem(key) || "null") ?? fallback
    } catch {
        return fallback
    }
}

function SkeletonCard() {
    return (
        <div className="room-skeleton-card">
            <div className="room-skeleton-card__header">
                <Skeleton.Item style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0 }} />
                <Skeleton.Item style={{ width: "40%", height: 12, borderRadius: 6 }} />
            </div>
            <Skeleton.Item style={{ width: "100%", height: 12, borderRadius: 6 }} />
            <Skeleton.Item style={{ width: "80%", height: 12, borderRadius: 6, alignSelf: "center" }} />
        </div>
    )
}

function SkeletonCards(): JSX.Element {
    const containerRef = useRef<HTMLDivElement>(null)
    const [roomsLength] = useState(Number(localStorage.getItem(ROOMS_LENGTH_SKELETON) || 30))
    const [positions] = useState<SavedPosition[]>(() => read<SavedPosition[]>(ROOMS_POSITIONS_SKELETON, []))
    const [grid] = useState<SavedGrid | null>(() => read<SavedGrid | null>(ROOMS_GRID_SKELETON, null))
    const [savedSize] = useState<RoomCardSizes | null>(
        () => (localStorage.getItem(ROOMS_SIZE_SKELETON) as RoomCardSizes) || null
    )

    // El tamaño medido solo se usa en el modo de respaldo: en la cuadrícula la card llena
    // su celda, así que el tamaño lo define el layout y no hace falta elegirlo.
    const { size: measuredSize } = useSetSkeletonCardSize(containerRef)
    const fallbackCard = CARD_SIZES[savedSize || measuredSize] || CARD_SIZES.xl

    // Si ya se cargaron las habitaciones alguna vez, se reproduce su misma cuadrícula para
    // que cada card del skeleton quede donde va la real y no haya salto al llegar los datos.
    if (positions.length && grid) {
        return (
            <div className="room-cards__wrapper__wrapper scrollbar__light-fat" ref={containerRef}>
                <div
                    className="room-cards__wrapper"
                    style={{ gridTemplate: `repeat(${grid.x}, 1fr) / repeat(${grid.y}, 1fr)` }}
                >
                    {positions.map((pos, index) => (
                        <div key={index} style={{ gridArea: `${pos.y}/ ${pos.x} / span 1 / span 1` }}>
                            <SkeletonCard />
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    return (
        <div className="home-screen-skeleton-container" ref={containerRef}>
            <div className="room-skeleton-cards">
                {Array.from({ length: roomsLength }, (_, index) => (
                    <div key={index} style={{ width: fallbackCard.width, height: fallbackCard.height }}>
                        <SkeletonCard />
                    </div>
                ))}
            </div>
        </div>
    )
}

export default SkeletonCards
